// BusyBee plans. The numbers (prices, credits, limits) come from the backend's
// config/plans.json via /api/config/plans (usePlanCatalog); FALLBACK_PLANS only
// covers the first render. Texts live in public/locales/<lang>/landing.json
// under pricing.* (plans, features, credits, faq).
import { useQuery } from '@tanstack/react-query';
import { configApi } from '../services/api';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';

export const TIER_FREE = 'free';
export const TIER_BASIC = 'basic';
export const TIER_PRO = 'pro';
export const TIER_MAX = 'max';
export const TIER_TEAM = 'team';

export const PLAN_ORDER = [TIER_FREE, TIER_BASIC, TIER_PRO, TIER_MAX, TIER_TEAM];
export const PLAN_RANK = { free: 0, basic: 1, pro: 2, team: 2, max: 3 };
const HIGHLIGHT = TIER_PRO;

const GB = 1024 ** 3;
export const FALLBACK_PLANS = {
  free: { price_monthly_usd: 0, price_yearly_usd: 0, bot_credits_monthly: 30, bots_max: 1, projects: 3, tasks: 100, documents: 5, storage_bytes: 250 * 1024 ** 2, bot_model_tiers: ['cheap'] },
  basic: { price_monthly_usd: 10, price_yearly_usd: 8, bot_credits_monthly: 380, bots_max: 3, storage_bytes: 5 * GB, team_seats_included: 7, bot_model_tiers: ['cheap', 'standard'] },
  pro: { price_monthly_usd: 29, price_yearly_usd: 23, bot_credits_monthly: 1150, bots_max: 10, storage_bytes: 25 * GB, team_seats_included: 25, computers_at_once: 3, bot_schedules: 3, bot_parallel_runs: 3, bot_byok: true, bot_model_tiers: ['cheap', 'standard'] },
  max: { price_monthly_usd: 99, price_yearly_usd: 79, bot_credits_monthly: 4000, bots_max: null, storage_bytes: 100 * GB, team_seats_included: 25, computers_at_once: 10, bot_schedules: 20, bot_parallel_runs: 10, bot_byok: true, bot_model_tiers: ['cheap', 'standard', 'premium'] },
  team: { price_monthly_usd: 25, price_yearly_usd: 20, per_seat: true, min_seats: 3, bot_credits_monthly: 1000, bots_max: null, storage_bytes: 10 * GB, computers_at_once: 3, bot_schedules: 20, bot_parallel_runs: 3, bot_byok: true, bot_model_tiers: ['cheap', 'standard'] },
};

// What credits buy, measured on real runs (1 credit = what USD 0.01 of AI costs us,
// OpenRouter prices of 2026-09). Shown on the pricing page.
export const CREDIT_EXAMPLES = [
  { key: 'quick_add', credits: '0.1' },
  { key: 'chat_fast', credits: '1' },
  { key: 'chat_standard', credits: '3' },
  { key: 'chat_premium', credits: '5' },
  { key: 'task_fast', credits: '2' },
  { key: 'search', credits: '0.5' },
  { key: 'computer_hour', credits: '17' },
];
export const CREDIT_PACK = { credits: 1000, usd: 19, eur: 19 };

export const pricingFaqKeys = ['try_free', 'credits', 'credits_run_out', 'trial_ends', 'change_plans', 'annual_discount', 'teams'];

/**
 * The currency a person pays in: the one Stripe already fixed for them, else EUR
 * in Europe (by the browser's time zone) and USD elsewhere. Prices include VAT.
 */
export const displayCurrency = (user) => {
  if (user?.billing_currency) return user.billing_currency;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.startsWith('Europe/') ? 'eur' : 'usd';
  } catch {
    return 'usd';
  }
};

/** What to tell a person whose checkout was refused (the backend's detail), or null for the generic text. */
export const checkoutErrorText = (t, detail) => {
  if (typeof detail === 'string') return detail;
  if (detail?.error === 'store_subscription') {
    return t('subscription.managed_in_store', { ns: 'common', store: detail.store === 'play_store' ? 'Google Play' : 'App Store' });
  }
  return null;
};

export const formatMoney = (amount, currency, lang) => {
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
};

const toPlan = (id, l, currency) => ({
  id,
  limits: l,
  currency,
  priceMonthly: l[`price_monthly_${currency}`] ?? l.price_monthly_usd ?? 0,
  priceYearly: l[`price_yearly_${currency}`] ?? l.price_yearly_usd ?? 0,
  perSeat: !!l.per_seat,
  minSeats: l.min_seats || 1,
  highlight: id === HIGHLIGHT,
  hasBadge: id === HIGHLIGHT,
});

/** The plans in order, with the server's numbers once they arrive. */
export const usePlanCatalog = (currency = 'usd') => {
  const { data } = useQuery({
    queryKey: ['plans-config'],
    queryFn: () => configApi.getPlans().then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });
  const source = data || FALLBACK_PLANS;
  return PLAN_ORDER.filter((id) => source[id]).map((id) => toPlan(id, source[id], currency));
};

/** Monthly prices for texts that name them (FAQ, description): {basic, pro, max, team}. */
export const priceParams = (plans, currency, lang) => Object.fromEntries(
  plans.filter((p) => p.priceMonthly > 0).map((p) => [p.id, formatMoney(p.priceMonthly, currency, lang)]));

export const formatStorage = (bytes, lang) => {
  if (bytes == null) return null;
  const gb = bytes / GB;
  const [value, unit] = gb >= 1 ? [Math.round(gb), 'gigabyte'] : [Math.round(bytes / 1024 ** 2), 'megabyte'];
  try {
    return new Intl.NumberFormat(lang, { style: 'unit', unit, unitDisplay: 'short' }).format(value);
  } catch {
    return `${value} ${unit === 'gigabyte' ? 'GB' : 'MB'}`;
  }
};

/** A plan's feature list in the viewer's language (t from the 'landing' namespace). */
export const planFeatures = (t, id, l, lang) => {
  const n = (v) => Number(v).toLocaleString(lang);
  const f = (key, params) => t(`pricing.features.${key}`, params);
  const tiers = l.bot_model_tiers || [];
  const models = tiers.includes('premium') ? f('models_all') : tiers.includes('standard') ? f('models_standard') : f('models_fast');
  const bots = l.bots_max == null ? f('bots_unlimited') : l.bots_max === 1 ? f('bots_one') : f('bots_n', { count: l.bots_max });
  const storage = formatStorage(l.storage_bytes, lang);
  const out = [];
  if (id === TIER_MAX || id === TIER_TEAM) out.push(f('everything_pro'));
  out.push(l.per_seat ? f('credits_team', { credits: n(l.bot_credits_monthly) }) : f('credits', { credits: n(l.bot_credits_monthly) }));
  out.push(bots, models);
  if (l.bot_byok && id === TIER_PRO) out.push(f('byok'));
  if (l.computers_at_once) out.push(l.per_seat ? f('computer_team', { count: l.computers_at_once }) : f('computer', { count: l.computers_at_once }));
  if (l.bot_schedules) out.push(f('schedules', { count: l.bot_schedules }));
  if ((l.bot_parallel_runs || 1) > 1) out.push(l.per_seat ? f('parallel_team', { count: l.bot_parallel_runs }) : f('parallel', { count: l.bot_parallel_runs }));
  if (id === TIER_FREE) {
    out.push(f('free_limits', { projects: l.projects, tasks: n(l.tasks), documents: l.documents }), f('list_view'));
  }
  if (id === TIER_BASIC) out.push(f('unlimited_work'), f('all_views'), ...(GOOGLE_APP_INTEGRATIONS ? [f('gcal')] : []));
  if (id === TIER_PRO) out.push(GOOGLE_APP_INTEGRATIONS ? f('gmail_webhooks') : f('webhooks'));
  if (l.team_seats_included > 1 && !l.per_seat) out.push(f('members', { count: l.team_seats_included }));
  if (storage) out.push(l.per_seat ? f('storage_team', { size: storage }) : f('storage', { size: storage }));
  return out;
};
